import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { track } from '@/analytics/tracker';
import {
  EMPTY_FILTERS,
  filtersFromSearchParams,
  filtersSnapshot,
  filtersToSearchParams,
  type CatalogFilters,
  type FilterGroupKey,
} from './filterModel';

/**
 * Фильтры и поиск живут в адресе (CAT-4): ссылку можно отправить. Обновления
 * идут через replaceState, чтобы «Назад» не листал историю фильтров.
 */
export function useCatalogUrlState() {
  const [params, setParams] = useSearchParams();
  const { filters, query } = useMemo(() => filtersFromSearchParams(params), [params]);

  const setQuery = useCallback(
    (next: string) => {
      setParams(
        (prev) =>
          filtersToSearchParams(
            filtersFromSearchParams(prev).filters,
            next.trim() ? next : '',
            prev,
          ),
        { replace: true },
      );
    },
    [setParams],
  );

  const setGroup = useCallback(
    (
      key: FilterGroupKey,
      values: CatalogFilters[FilterGroupKey],
      action: 'add' | 'remove' | 'set',
      value?: string | number,
    ) => {
      setParams(
        (prev) => {
          const current = filtersFromSearchParams(prev);
          const nextFilters = { ...current.filters, [key]: values } as CatalogFilters;
          track('filter_change', {
            metadata: { filter: key, action, value, snapshot: filtersSnapshot(nextFilters) },
          });
          return filtersToSearchParams(nextFilters, current.query, prev);
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const resetFilters = useCallback(() => {
    setParams(
      (prev) => {
        const current = filtersFromSearchParams(prev);
        track('filters_cleared', { metadata: { snapshot: filtersSnapshot(current.filters) } });
        return filtersToSearchParams(EMPTY_FILTERS, current.query, prev);
      },
      { replace: true },
    );
  }, [setParams]);

  /**
   * Подсказка поиска «показать всю категорию»: категория выбирается, а текст запроса снимается одним
   * обновлением адреса — два вызова подряд затёрли бы друг друга (setSearchParams не ставит их в очередь).
   */
  const showCategory = useCallback(
    (categoryId: number) => {
      setParams(
        (prev) => {
          const current = filtersFromSearchParams(prev);
          const nextFilters: CatalogFilters = { ...current.filters, category_ids: [categoryId] };
          track('filter_change', {
            metadata: {
              filter: 'category_ids',
              action: 'set',
              value: categoryId,
              snapshot: filtersSnapshot(nextFilters),
            },
          });
          return filtersToSearchParams(nextFilters, '', prev);
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return { filters, query, setQuery, setGroup, resetFilters, showCategory };
}
